import { defineStore } from 'pinia';
import axios from 'axios';

export const FRONT_SETTINGS_ENDPOINT = 'front-settings';

// Decorator-inspired helpers for schema definitions in plain JavaScript.
export const JsonKey = (key) => ({ jsonKey: key });
export const Default = (value) => ({ defaultValue: value });

const stringValue = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    return String(value).trim();
};

const nullableStringValue = (value) => {
    const normalized = stringValue(value, '');
    return normalized === '' ? null : normalized;
};

const booleanValue = (value, fallback = false) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
        if (['0', 'false', 'no', 'off', ''].includes(normalized)) return false;
    }
    return fallback;
};

const clone = (value) => JSON.parse(JSON.stringify(value));

export const FRONT_SETTINGS_FIELDS = {
    email: { ...JsonKey('email'), ...Default(''), transform: stringValue },
    address: { ...JsonKey('address'), ...Default(''), transform: stringValue },
    firstPhone: { ...JsonKey('first_phone'), ...Default(''), transform: stringValue },
    secondPhone: { ...JsonKey('second_phone'), ...Default(''), transform: stringValue },
    disponibilities: { ...JsonKey('disponibilities'), ...Default(''), transform: stringValue },
    whatsappLabel: { ...JsonKey('whatsapp_label'), ...Default(''), transform: stringValue },
    whatsappPhone: { ...JsonKey('whatsapp'), ...Default(''), transform: stringValue },
    instagramPrimaryLabel: { ...JsonKey('first_instagram_label'), ...Default(''), transform: stringValue },
    instagramPrimaryUrl: { ...JsonKey('instagram'), ...Default(''), transform: stringValue },
    instagramSecondaryLabel: { ...JsonKey('second_instagram_label'), ...Default(''), transform: stringValue },
    instagramSecondaryUrl: { ...JsonKey('second_instagram'), ...Default(''), transform: stringValue },
    facebookPrimaryLabel: { ...JsonKey('first_facebook_label'), ...Default(''), transform: stringValue },
    facebookPrimaryUrl: { ...JsonKey('facebook'), ...Default(''), transform: stringValue },
    facebookSecondaryLabel: { ...JsonKey('second_facebook_label'), ...Default(''), transform: stringValue },
    facebookSecondaryUrl: { ...JsonKey('second_facebook'), ...Default(''), transform: stringValue },
    hasToolsAccess: { ...JsonKey('has_tools_access'), ...Default(false), transform: booleanValue },
    toolsThumbnailUrl: { ...JsonKey('tools_thumbnail'), ...Default(null), transform: nullableStringValue },
    hasMeetingAccess: { ...JsonKey('has_meeting_access'), ...Default(false), transform: booleanValue },
    hasChatAccess: { ...JsonKey('has_chat_access'), ...Default(false), transform: booleanValue },
    hasPlanningAccess: { ...JsonKey('has_planing_access'), ...Default(false), transform: booleanValue }
};

export const createDefaultFrontSettings = () => ({
    contact: {
        email: FRONT_SETTINGS_FIELDS.email.defaultValue,
        address: FRONT_SETTINGS_FIELDS.address.defaultValue,
        firstPhone: FRONT_SETTINGS_FIELDS.firstPhone.defaultValue,
        secondPhone: FRONT_SETTINGS_FIELDS.secondPhone.defaultValue
    },
    availability: {
        disponibilities: FRONT_SETTINGS_FIELDS.disponibilities.defaultValue
    },
    whatsapp: {
        label: FRONT_SETTINGS_FIELDS.whatsappLabel.defaultValue,
        phone: FRONT_SETTINGS_FIELDS.whatsappPhone.defaultValue,
        url: null
    },
    socialMedia: {
        instagram: {
            primary: {
                label: FRONT_SETTINGS_FIELDS.instagramPrimaryLabel.defaultValue,
                url: FRONT_SETTINGS_FIELDS.instagramPrimaryUrl.defaultValue
            },
            secondary: {
                label: FRONT_SETTINGS_FIELDS.instagramSecondaryLabel.defaultValue,
                url: FRONT_SETTINGS_FIELDS.instagramSecondaryUrl.defaultValue
            }
        },
        facebook: {
            primary: {
                label: FRONT_SETTINGS_FIELDS.facebookPrimaryLabel.defaultValue,
                url: FRONT_SETTINGS_FIELDS.facebookPrimaryUrl.defaultValue
            },
            secondary: {
                label: FRONT_SETTINGS_FIELDS.facebookSecondaryLabel.defaultValue,
                url: FRONT_SETTINGS_FIELDS.facebookSecondaryUrl.defaultValue
            }
        }
    },
    access: {
        tools: FRONT_SETTINGS_FIELDS.hasToolsAccess.defaultValue,
        meeting: FRONT_SETTINGS_FIELDS.hasMeetingAccess.defaultValue,
        chat: FRONT_SETTINGS_FIELDS.hasChatAccess.defaultValue,
        planning: FRONT_SETTINGS_FIELDS.hasPlanningAccess.defaultValue
    },
    assets: {
        toolsThumbnailUrl: FRONT_SETTINGS_FIELDS.toolsThumbnailUrl.defaultValue
    }
});

export const buildWhatsappUrl = (phone) => {
    const digits = stringValue(phone).replace(/[^\d+]/g, '');
    return digits ? `https://wa.me/${digits.replace(/^\+/, '')}` : null;
};

export const normalizeFrontSettings = (payload = {}) => {
    const source = payload && typeof payload === 'object' ? payload : {};
    const getValue = (field) => field.transform(source[field.jsonKey], field.defaultValue);

    return {
        contact: {
            email: getValue(FRONT_SETTINGS_FIELDS.email),
            address: getValue(FRONT_SETTINGS_FIELDS.address),
            firstPhone: getValue(FRONT_SETTINGS_FIELDS.firstPhone),
            secondPhone: getValue(FRONT_SETTINGS_FIELDS.secondPhone)
        },
        availability: {
            disponibilities: getValue(FRONT_SETTINGS_FIELDS.disponibilities)
        },
        whatsapp: {
            label: getValue(FRONT_SETTINGS_FIELDS.whatsappLabel),
            phone: getValue(FRONT_SETTINGS_FIELDS.whatsappPhone),
            url: buildWhatsappUrl(getValue(FRONT_SETTINGS_FIELDS.whatsappPhone))
        },
        socialMedia: {
            instagram: {
                primary: {
                    label: getValue(FRONT_SETTINGS_FIELDS.instagramPrimaryLabel),
                    url: getValue(FRONT_SETTINGS_FIELDS.instagramPrimaryUrl)
                },
                secondary: {
                    label: getValue(FRONT_SETTINGS_FIELDS.instagramSecondaryLabel),
                    url: getValue(FRONT_SETTINGS_FIELDS.instagramSecondaryUrl)
                }
            },
            facebook: {
                primary: {
                    label: getValue(FRONT_SETTINGS_FIELDS.facebookPrimaryLabel),
                    url: getValue(FRONT_SETTINGS_FIELDS.facebookPrimaryUrl)
                },
                secondary: {
                    label: getValue(FRONT_SETTINGS_FIELDS.facebookSecondaryLabel),
                    url: getValue(FRONT_SETTINGS_FIELDS.facebookSecondaryUrl)
                }
            }
        },
        access: {
            tools: getValue(FRONT_SETTINGS_FIELDS.hasToolsAccess),
            meeting: getValue(FRONT_SETTINGS_FIELDS.hasMeetingAccess),
            chat: getValue(FRONT_SETTINGS_FIELDS.hasChatAccess),
            planning: getValue(FRONT_SETTINGS_FIELDS.hasPlanningAccess)
        },
        assets: {
            toolsThumbnailUrl: getValue(FRONT_SETTINGS_FIELDS.toolsThumbnailUrl)
        }
    };
};

export const flattenFrontSettings = (settings = createDefaultFrontSettings()) => ({
    email: stringValue(settings.contact?.email),
    address: stringValue(settings.contact?.address),
    first_phone: stringValue(settings.contact?.firstPhone),
    second_phone: stringValue(settings.contact?.secondPhone),
    disponibilities: stringValue(settings.availability?.disponibilities),
    whatsapp_label: stringValue(settings.whatsapp?.label),
    whatsapp: stringValue(settings.whatsapp?.phone),
    instagram: stringValue(settings.socialMedia?.instagram?.primary?.url),
    first_instagram_label: stringValue(settings.socialMedia?.instagram?.primary?.label),
    second_instagram: stringValue(settings.socialMedia?.instagram?.secondary?.url),
    second_instagram_label: stringValue(settings.socialMedia?.instagram?.secondary?.label),
    facebook: stringValue(settings.socialMedia?.facebook?.primary?.url),
    first_facebook_label: stringValue(settings.socialMedia?.facebook?.primary?.label),
    second_facebook: stringValue(settings.socialMedia?.facebook?.secondary?.url),
    second_facebook_label: stringValue(settings.socialMedia?.facebook?.secondary?.label),
    has_tools_access: booleanValue(settings.access?.tools),
    has_meeting_access: booleanValue(settings.access?.meeting),
    has_chat_access: booleanValue(settings.access?.chat),
    has_planing_access: booleanValue(settings.access?.planning),
    tools_thumbnail: nullableStringValue(settings.assets?.toolsThumbnailUrl)
});

export const resolveSelectedCompanyId = () => {
    try {
        const company = JSON.parse(globalThis?.localStorage?.getItem('selectedCompany') || 'null');
        return company?.id ?? null;
    } catch (_) {
        return null;
    }
};

const frontSettingsState = () => ({
    settings: createDefaultFrontSettings(),
    raw: null,
    loading: false,
    updating: false,
    loaded: false,
    error: null,
    message: '',
    companyId: null,
    lastFetchedAt: null
});

export const useFrontSettingsStore = defineStore('front-settings', {
    state: frontSettingsState,

    getters: {
        isLoading: (state) => state.loading,
        isUpdating: (state) => state.updating,
        hasLoaded: (state) => state.loaded,
        getError: (state) => state.error,
        getMessage: (state) => state.message,
        getSettings: (state) => state.settings,
        getRaw: (state) => state.raw,
        getContactInfo: (state) => state.settings.contact,
        getAvailability: (state) => state.settings.availability.disponibilities,
        getWhatsapp: (state) => state.settings.whatsapp,
        getSocialMedia: (state) => state.settings.socialMedia,
        getAccessFlags: (state) => state.settings.access,
        getToolsThumbnailUrl: (state) => state.settings.assets.toolsThumbnailUrl,
        getEmail: (state) => state.settings.contact.email,
        getAddress: (state) => state.settings.contact.address,
        getFirstPhone: (state) => state.settings.contact.firstPhone,
        getSecondPhone: (state) => state.settings.contact.secondPhone,
        getDisponibilities: (state) => state.settings.availability.disponibilities,
        getWhatsappLabel: (state) => state.settings.whatsapp.label,
        getWhatsappPhone: (state) => state.settings.whatsapp.phone,
        getWhatsappUrl: (state) => state.settings.whatsapp.url,
        getInstagramPrimary: (state) => state.settings.socialMedia.instagram.primary,
        getInstagramSecondary: (state) => state.settings.socialMedia.instagram.secondary,
        getFacebookPrimary: (state) => state.settings.socialMedia.facebook.primary,
        getFacebookSecondary: (state) => state.settings.socialMedia.facebook.secondary,
        canUseTools: (state) => state.settings.access.tools,
        canUseMeeting: (state) => state.settings.access.meeting,
        canUseChat: (state) => state.settings.access.chat,
        canUsePlanning: (state) => state.settings.access.planning,
        hasAnySocialLinks: (state) => {
            const links = [
                state.settings.socialMedia.instagram.primary.url,
                state.settings.socialMedia.instagram.secondary.url,
                state.settings.socialMedia.facebook.primary.url,
                state.settings.socialMedia.facebook.secondary.url
            ];
            return links.some((link) => !!stringValue(link));
        }
    },

    actions: {
        reset() {
            Object.assign(this, frontSettingsState());
        },

        applyPayload(payload) {
            const normalized = normalizeFrontSettings(payload);
            this.settings = normalized;
            this.raw = payload ? clone(payload) : null;
            this.loaded = true;
            this.lastFetchedAt = new Date().toISOString();
            return normalized;
        },

        updateSettings(updater) {
            const nextSettings = typeof updater === 'function'
                ? updater(clone(this.settings))
                : { ...this.settings, ...(updater || {}) };
            this.settings = normalizeFrontSettings(flattenFrontSettings(nextSettings));
            return this.settings;
        },

        setContactField(field, value) {
            this.settings = {
                ...this.settings,
                contact: {
                    ...this.settings.contact,
                    [field]: stringValue(value)
                }
            };
        },

        setAvailability(value) {
            this.settings = {
                ...this.settings,
                availability: {
                    ...this.settings.availability,
                    disponibilities: stringValue(value)
                }
            };
        },

        setWhatsappField(field, value) {
            const whatsapp = {
                ...this.settings.whatsapp,
                [field]: stringValue(value)
            };
            whatsapp.url = buildWhatsappUrl(whatsapp.phone);
            this.settings = {
                ...this.settings,
                whatsapp
            };
        },

        setSocialField(network, account, field, value) {
            this.settings = {
                ...this.settings,
                socialMedia: {
                    ...this.settings.socialMedia,
                    [network]: {
                        ...this.settings.socialMedia[network],
                        [account]: {
                            ...this.settings.socialMedia[network][account],
                            [field]: stringValue(value)
                        }
                    }
                }
            };
        },

        setAccessField(field, value) {
            this.settings = {
                ...this.settings,
                access: {
                    ...this.settings.access,
                    [field]: booleanValue(value)
                }
            };
        },

        setToolsThumbnailUrl(value) {
            this.settings = {
                ...this.settings,
                assets: {
                    ...this.settings.assets,
                    toolsThumbnailUrl: nullableStringValue(value)
                }
            };
        },

        setEmail(value) { this.setContactField('email', value); },
        setAddress(value) { this.setContactField('address', value); },
        setFirstPhone(value) { this.setContactField('firstPhone', value); },
        setSecondPhone(value) { this.setContactField('secondPhone', value); },
        setDisponibilities(value) { this.setAvailability(value); },
        setWhatsappLabel(value) { this.setWhatsappField('label', value); },
        setWhatsappPhone(value) { this.setWhatsappField('phone', value); },
        setInstagramPrimaryLabel(value) { this.setSocialField('instagram', 'primary', 'label', value); },
        setInstagramPrimaryUrl(value) { this.setSocialField('instagram', 'primary', 'url', value); },
        setInstagramSecondaryLabel(value) { this.setSocialField('instagram', 'secondary', 'label', value); },
        setInstagramSecondaryUrl(value) { this.setSocialField('instagram', 'secondary', 'url', value); },
        setFacebookPrimaryLabel(value) { this.setSocialField('facebook', 'primary', 'label', value); },
        setFacebookPrimaryUrl(value) { this.setSocialField('facebook', 'primary', 'url', value); },
        setFacebookSecondaryLabel(value) { this.setSocialField('facebook', 'secondary', 'label', value); },
        setFacebookSecondaryUrl(value) { this.setSocialField('facebook', 'secondary', 'url', value); },
        setHasToolsAccess(value) { this.setAccessField('tools', value); },
        setHasMeetingAccess(value) { this.setAccessField('meeting', value); },
        setHasChatAccess(value) { this.setAccessField('chat', value); },
        setHasPlanningAccess(value) { this.setAccessField('planning', value); },

        getSnapshot() {
            return clone(this.settings);
        },

        async fetchSettings(companyId = null) {
            this.loading = true;
            this.error = null;
            const resolvedCompanyId = companyId ?? resolveSelectedCompanyId();
            this.companyId = resolvedCompanyId;
            try {
                const config = resolvedCompanyId ? { params: { company_id: resolvedCompanyId } } : {};
                const response = await axios.get(FRONT_SETTINGS_ENDPOINT, config);
                const payload = response?.data?.data ?? {};
                this.message = response?.data?.message || '';
                this.applyPayload(payload);
                return this.settings;
            } catch (error) {
                this.error = error?.response?.data?.message || error?.message || 'Unable to load front settings';
                throw error;
            } finally {
                this.loading = false;
            }
        }
    }
});
